'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { ShopperBasketsV2 } from '@/scapi';
import { useToast } from '@/components/toast';
import { useAnalytics } from '@/hooks/use-analytics';
import { setMiniCartOpen } from '@/hooks/mini-cart-store';
import { useBasketUpdater } from '@/providers/basket';
import { resourceRoutes } from '@/route-paths';

type Basket = ShopperBasketsV2.schemas['Basket'];
type AddToBagResponse = { success?: boolean; basket?: unknown; error?: { message?: string } };

/**
 * Adds one unit of `productId` to the bag, mirroring the add path of `useProductActions` without
 * its PDP state: refresh the basket and open the minibag on success, toast on failure.
 */
export function useProductAddToBag(productId: string | undefined) {
    const { t } = useTranslation('product');
    const { addToast } = useToast();
    const analytics = useAnalytics();
    const updateBasket = useBasketUpdater();
    const fetcher = useFetcher<AddToBagResponse>();
    const [isAdding, setIsAdding] = useState(false);
    // fetcher.data survives between submissions; ignore the previous response when adding again.
    const previousDataRef = useRef<AddToBagResponse | undefined>(undefined);

    useEffect(() => {
        const data = fetcher.data;
        if (!isAdding || data === previousDataRef.current) return;
        if (data?.success && data.basket) {
            updateBasket(data.basket as Basket);
            setMiniCartOpen(true);
            setIsAdding(false);
        } else if (data?.success === false) {
            addToast(t('failedToAddToCart', { error: data.error?.message }), 'error');
            setIsAdding(false);
        }
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- addToast/t/updateBasket are stable
    }, [isAdding, fetcher.data]);

    const addToBag = useCallback(async () => {
        if (!productId) {
            addToast(t('failedToAddProductToCart'), 'error');
            return;
        }
        previousDataRef.current = fetcher.data;
        setIsAdding(true);
        const productItem = { productId, quantity: 1 };
        try {
            await fetcher.submit(
                { productItem: JSON.stringify(productItem) },
                { method: 'POST', action: resourceRoutes.cartItemAdd }
            );
            void analytics.trackCartItemAdd({ cartItems: [productItem as ShopperBasketsV2.schemas['ProductItem']] });
        } catch {
            setIsAdding(false);
            addToast(t('failedToAddProductToCart'), 'error');
        }
    }, [productId, fetcher, analytics, addToast, t]);

    return { addToBag, isAdding };
}
